import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { logCustomerInteraction, renderMessageTemplate } from '../api/client';
import { normalizeIsraeliPhoneNumber, normalizePhoneForWhatsAppLink } from '../lib/phone';
import EditMessageTemplateModal from './EditMessageTemplateModal';

interface DocumentWhatsAppModalProps {
  document: {
    document_type_name: string;
    document_number: number;
    document_date: string;
    amount: number;
    open_amount?: number;
    customer_id?: number;
    customer_name: string;
    document_link?: string;
  };
  phone?: string | null;
  onClose: () => void;
}

export default function DocumentWhatsAppModal({ document, phone, onClose }: DocumentWhatsAppModalProps) {
  const { t } = useTranslation();
  const [phoneNumber, setPhoneNumber] = useState(phone || '');
  const [message, setMessage] = useState('');
  const [templateRevision, setTemplateRevision] = useState(0);
  const [audit, setAudit] = useState<{
    requestId: string;
    opened: boolean;
    phone: string;
    message: string;
    documentReference: string;
  } | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const formatAmount = (amount: number) =>
    new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS' }).format(amount);

  const customerName = document.customer_name || (document.customer_id ? `#${document.customer_id}` : '');
  const normalizedPhone = normalizeIsraeliPhoneNumber(phoneNumber);
  const amount = formatAmount(document.open_amount ?? document.amount)
    .replace(/[^\d.,]/g, '')
    .replace(',', '.');
  const templateValues = useMemo(() => ({
    customer_name: customerName,
    document_type_name: document.document_type_name,
    document_number: String(document.document_number),
    amount,
    document_url: document.document_link || '',
  }), [amount, customerName, document.document_link, document.document_number, document.document_type_name]);

  useEffect(() => {
    if (!document.document_link) {
      setMessage('');
      return;
    }
    let active = true;
    renderMessageTemplate('document_whatsapp', templateValues)
      .then((response) => {
        if (active) setMessage(response.data.body);
      })
      .catch(() => {
        if (active) setMessage('');
      });
    return () => {
      active = false;
    };
  }, [document.document_link, templateRevision, templateValues]);

  const handleSend = async () => {
    if (!document.customer_id || !normalizedPhone || !document.document_link || !message) return;
    const attempt = audit || {
      requestId: crypto.randomUUID(),
      opened: false,
      phone: phoneNumber,
      message,
      documentReference: `${document.document_type_name} ${document.document_number}`,
    };
    setError(null);
    setSaving(true);
    if (!attempt.opened) {
      window.open(`https://wa.me/${normalizePhoneForWhatsAppLink(attempt.phone)}?text=${encodeURIComponent(attempt.message)}`, '_blank', 'noopener,noreferrer');
      attempt.opened = true;
      setAudit(attempt);
    }
    try {
      await logCustomerInteraction(document.customer_id, {
        channel: 'whatsapp',
        message_type: 'document_sent',
        selected_action: 'document_handoff_opened',
        destination: attempt.phone,
        message_text: attempt.message,
        related_invoice_ids: [attempt.documentReference],
        request_id: attempt.requestId,
      });
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.detail || t('customerBot.interactionSaveError', 'WhatsApp opened, but communication history could not be saved. Press again to retry saving without opening another draft.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-950/45 p-4" onClick={onClose}>
      <div
        className="flex max-h-[90vh] w-full max-w-xl flex-col rounded-xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-start justify-between gap-4 border-b border-gray-100 px-5 py-4">
          <div>
            <p className="text-sm font-medium text-green-700">{t('documents.sendDocumentWhatsAppTitle', 'Send document by WhatsApp')}</p>
            <h2 className="text-lg font-bold text-gray-900">{customerName || t('documents.customer', 'Customer')}</h2>
            <p className="mt-1 text-sm text-gray-500">
              {t('documents.sendDocumentConfirm', 'Confirm the document and destination number before sending.')}
            </p>
          </div>
          {error && <div role="alert" className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}
          <button type="button" onClick={onClose} className="rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-500 hover:bg-gray-50">
            {t('common.close', 'Close')}
          </button>
        </div>

        <div className="space-y-4 overflow-y-auto px-5 py-5">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
              <div className="text-xs font-medium text-gray-500">{t('documents.type')}</div>
              <div className="mt-1 font-semibold text-gray-900">{document.document_type_name}</div>
            </div>
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
              <div className="text-xs font-medium text-gray-500">{t('documents.docNumber')}</div>
              <div className="mt-1 font-semibold text-gray-900">{document.document_number}</div>
            </div>
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
              <div className="text-xs font-medium text-gray-500">{t('documents.date')}</div>
              <div className="mt-1 font-semibold text-gray-900">{document.document_date}</div>
            </div>
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
              <div className="text-xs font-medium text-gray-500">{t('documents.amount')}</div>
              <div className="mt-1 font-semibold text-gray-900">{formatAmount(document.amount)}</div>
            </div>
          </div>

          {document.open_amount != null && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
              <div className="text-xs font-medium text-amber-700">{t('documents.openAmount', 'Open balance')}</div>
              <div className="mt-1 text-lg font-bold text-amber-900">{formatAmount(document.open_amount)}</div>
            </div>
          )}

          <label className="block">
            <span className="mb-1 block text-sm font-medium text-gray-700">{t('documents.whatsAppDestinationNumber', 'WhatsApp number')}</span>
            <input
              type="tel"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              placeholder={t('documents.whatsAppNumberPlaceholder', 'e.g. 050-1234567')}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500"
            />
            <span className="mt-1 block text-xs text-gray-500">
              {normalizedPhone
                ? t('documents.normalizedWhatsAppNumber', 'Will send to {{phone}}', { phone: normalizedPhone })
                : t('documents.noPhoneNumber')}
            </span>
          </label>

          <div>
            <div className="mb-2 flex items-center justify-between gap-3">
              <div className="text-sm font-medium text-gray-700">{t('reports.messagePreview', 'Message preview')}</div>
              <EditMessageTemplateModal
                templateKey="document_whatsapp"
                values={templateValues}
                onChanged={() => setTemplateRevision((current) => current + 1)}
              />
            </div>
            <div className="max-h-48 overflow-y-auto whitespace-pre-wrap rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm leading-6 text-gray-800">
              {message || t('settings.messageTemplates.previewUnavailable')}
            </div>
          </div>
        </div>

        <div className="flex shrink-0 flex-wrap justify-end gap-2 border-t border-gray-100 px-5 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
          >
            {t('common.cancel', 'Cancel')}
          </button>
          <button
            type="button"
            onClick={handleSend}
            disabled={!document.customer_id || !normalizedPhone || !document.document_link || !message || saving}
            className="rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:bg-gray-300"
          >
            {saving ? t('common.saving', 'Saving...') : t('documents.sendWhatsApp')}
          </button>
        </div>
      </div>
    </div>
  );
}
